import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Alert } from 'antd';
import { Button, FormField, Input } from '@shared/ui';
import { ROUTES } from '@shared/config/routes';
import { authApi } from '../api/auth-api';
import { registerSchema, type RegisterFormValues, detectChannel } from '../model/schemas';
import { applyApiFieldErrors } from '@shared/lib/forms/apply-field-errors';
import { mapAuthError } from '../lib/map-auth-error';

export function RegisterForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: { email: '', password: '' },
    mode: 'onBlur',
  });

  const mutation = useMutation({
    mutationFn: (values: RegisterFormValues) =>
      authApi.register({
        email: values.email,
        password: values.password || undefined,
      }),
    onSuccess: (_data, values) => {
      navigate(
        `${ROUTES.verify}?identity=${encodeURIComponent(values.email)}&channel=${detectChannel(values.email)}`,
      );
    },
    onError: (error) => {
      if (!applyApiFieldErrors(error, form.setError)) {
        setSubmitError(mapAuthError(error));
      }
    },
  });

  return (
    <FormProvider {...form}>
    <form
      className="flex flex-col gap-4"
      onSubmit={form.handleSubmit((v) => {
        setSubmitError(null);
        mutation.mutate(v);
      })}
      noValidate
    >
      <FormField
        label={t('common.email')}
        required
        error={form.formState.errors.email?.message}
      >
        {(p) => (
          <Input
            {...p}
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            {...form.register('email')}
          />
        )}
      </FormField>

      <FormField
        label={t('common.password')}
        hint={t('auth.register.passwordHint')}
        error={form.formState.errors.password?.message}
      >
        {(p) => (
          <Input
            {...p}
            type="password"
            autoComplete="new-password"
            {...form.register('password')}
          />
        )}
      </FormField>

      {submitError ? <Alert type="error" showIcon message={submitError} /> : null}

      <Button type="submit" loading={mutation.isPending}>
        {t('auth.register.submit')}
      </Button>
    </form>
    </FormProvider>
  );
}
